import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { fetchCertificateEvents } from 'store/slices';

import usePermission from 'hooks/usePermission';

import {
  getDataFromLocalStorage,
  getEventDate,
  getUserType,
  objectToFormData,
  paymentColor,
} from 'utils/helpers';

import { limit } from 'utils/constants';

import Card from 'components/Layout/Card';
import Table from 'components/Layout/Table';
import TabLayout from 'components/Layout/TabLayout';
import SeachInput from 'components/form/SeachInput';

import './CertificateRequest.scss';

const tabs = [
  { key: '1', label: 'Pending' },
  { key: '2', label: 'Completed' },
];

const CertificateRequest = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { viewAccess } = usePermission("Requests");

  const [childTabType, setChildTabType] = useState('1');
  const [isLoading, setIsLoading] = useState(true);
  const [tableList, setTableList] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [timer, setTimer] = useState('');
  const [searchPayload, setSearchPayload] = useState({
    event_name: '',
    event_type: '',
    membership_id_or_type: '',
  });
  const [filterData, setFilterData] = useState({
    total: 0,
    offset: 0,
    limit: limit,
    event_by_filter: '',
  });

  const { eventTypeList } = useSelector((state) => ({
    eventTypeList: state.global.eventTypeList,
  }));

  const userType = getDataFromLocalStorage('user_type');
  const urlType = ['0', '1'].includes(userType)
    ? 'request-received'
    : 'raised-request';

  const getEvents = async (obj) => {
    const updatedObj = { ...obj, tab_type: childTabType };
    let forData = objectToFormData(updatedObj);
    const response = await dispatch(fetchCertificateEvents(forData));
    setTableList(response?.data?.certificate_event || []);
    setFilterData({
      ...obj,
      total: response?.data?.result_count || 0,
    });
    setIsLoading(false);
  };

  useEffect(() => {
    getEvents();
    setIsLoading(true);
  }, [childTabType]);

  const handelChangeSearch = (searchData) => {
    setIsLoading(true);
    let newData = filterData;
    setSearchPayload(searchData);
    newData = { ...newData, ...searchData, offset: 0 };
    setFilterData(newData);
    getEvents(newData);
  };

  const handelChangePagination = (offset, newLimit) => {
    setIsLoading(true);
    const updatedData = {
      ...filterData,
      ...searchPayload,
      offset,
      limit: newLimit ?? filterData.limit,
    };
    setFilterData(updatedData);
    getEvents(updatedData);
  };

  // Debounced search handler
  const handleSearch = (e) => {
    let value = e?.target?.value;
    setSearchText(value);
    let time = timer;
    clearTimeout(time);
    time = setTimeout(() => {
      const updatedSearchPayload = { ...searchPayload, event_name: value };
      setSearchPayload(updatedSearchPayload);
      const updatedFilterData = {
        ...filterData,
        offset: 0,
        ...updatedSearchPayload,
      };
      setIsLoading(true);
      setFilterData(updatedFilterData);
      getEvents(updatedFilterData);
    }, 800);
    setTimer(time);
  };

  const header = [
    {
      isSearch: true,
      isSearchDropdown: true,
      dropdownOptions: { options: eventTypeList, key: 'id', value: 'name' },
      searchInputName: 'event_type',
      title: 'Event Type',
    },
    {
      isSearch: true,
      searchInputName: 'event_name',
      title: 'Event Name',
    },
    {
      isSearch: true,
      isDatePicker: true,
      searchInputName: 'date',
      title: 'Date & Venue',
    },
    {
      isSearch: true,
      isSearchDropdown: true,
      dropdownOptions: {
        options: [
          { name: 'Upcoming' },
          { name: 'Ongoing' },
          { name: 'Completed' },
        ],
        key: 'name',
        value: 'name',
      },
      searchInputName: 'status',
      title: 'Status',
    },

    {
      isSearch: true,
      isSearchDropdown: true,
      dropdownOptions: {
        options: [
          { name: 'Pending' },
          { name: 'In-Progress' },
          { name: 'Approved' },
          { name: 'Rejected' },
        ],
        key: 'name',
        value: 'name',
      },
      searchInputName: 'request_status',
      title: 'Request Status',
    },
  ];

  const rowData = [];
  tableList.forEach((elem) => {
    let obj = [
      {
        value: elem.event_type,
      },
      {
        value: (
          <div
            className="pointer color-new-car" //hover-effect
            onClick={() => {
              if (viewAccess === "1") {
                navigate(
                  `/${getUserType()}/${urlType}/certificates-event/request/${elem.id
                  }`
                );
              }
            }}
          >
            {elem.event_name}
          </div>
        ),
      },
      {
        value: (
          <span>
            <div>{getEventDate(elem.start_date, elem.end_date)}</div>
            {elem?.country_name && (
              <div>{`${elem?.city_name ? `${elem?.city_name},` : ''} ${elem?.country_name
                }`}</div>
            )}
          </span>
        ),
      },
      {
        value: elem.status,
      },
      {
        value: (
          <div className={paymentColor(elem.request_status)}>
            {elem.request_status}
          </div>
        ),
      },
    ];

    rowData.push({ data: obj });
  });

  return (
    <div id="certificate-request-container">
      <Card className="cps-20 cpe-20 cpb-20">
        <div className="cpt-14">
          <TabLayout
            tabs={tabs}
            activeKey={childTabType}
            onChange={setChildTabType}
          />
        </div>

        <div>
          <div className="d-flex justify-content-between align-items-center cpt-20 cpb-20">
            <div className="table-title">All Events ({filterData?.total})</div>
            <div style={{ width: '150px' }} className="me-3">
              <SeachInput
                placeholder="Search"
                value={searchText}
                onChange={handleSearch}
                className="small-text-input"
                iconClassName="small-search-icon"
              />
            </div>
          </div>
          <Table
            isLoading={isLoading}
            header={header}
            rowData={rowData}
            filterData={filterData}
            searchPayload={searchPayload}
            searchInputChange={handelChangeSearch}
            changeOffset={handelChangePagination}
            maxHeight="60vh"
            showRowsLimit={['0', '1'].includes(userType)}
          />
        </div>
      </Card>
    </div>
  );
};

export default CertificateRequest;
